
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RoyaltyReport } from '@/types';
import { useToast } from './use-toast';
import { normalizeCountryCode } from '@/utils/countries';

export const useQuarterlyReports = (authorId: string, year: number, quarter: number) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['quarterlyReports', authorId, year, quarter],
    queryFn: async (): Promise<RoyaltyReport[]> => {
      const { data, error } = await supabase
        .from('royalty_reports')
        .select(`
          *,
          books:book_id (
            title,
            isbn
          )
        `)
        .eq('author_id', authorId)
        .eq('year', year)
        .eq('quarter', quarter)
        .order('created_at', { ascending: false });
      
      if (error) {
        toast({
          title: 'Error loading quarterly reports',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data.map(report => ({
        id: report.id,
        quarter: report.quarter,
        year: report.year,
        authorId: report.author_id,
        bookId: report.book_id,
        salesCount: report.sales_count,
        amount: report.amount,
        currency: report.currency,
        status: report.status,
        createdAt: report.created_at,
        books: report.books,
        // Enhanced fields
        unitsSold: report.units_sold || report.sales_count || 0,
        grossSales: report.gross_sales || report.amount || 0,
        returns: report.returns || 0,
        netSales: report.net_sales !== undefined ? report.net_sales : 0, // Use actual net_sales, default to 0
        royaltyAmount: report.royalty_amount || report.amount || 0,
        bookFormat: report.book_format || 'paperback',
        country: normalizeCountryCode(report.country || 'US')
      })) as unknown as RoyaltyReport[];
    },
    enabled: !!authorId && !!year && !!quarter
  });
};
